import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { DatetimeService } from '../../../shared/services/datetime.service';
import {
  Deal,
  DealRequest,
  DealResponse,
  DealSearchParams,
  PageResponse,
  DealCycle,
  DealOrigin,
  DealBusinessType,
  DealPriority,
  DealBulkActionRequest,
  DealBulkActionResponse,
  DealDuplicateCheckResponse,
} from '../models/deal.model';
import { CrmUserResponse } from '../../users/models/user.model';
import { FullHttpParameterCodec } from '../../../shared/http/full-parameter-codec';

@Injectable({
  providedIn: 'root',
})
export class DealApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(
    private http: HttpClient,
    private datetimeService: DatetimeService,
  ) {}

  // GET /api/deals - List deals with filters and pagination
  searchDeals(
    params: DealSearchParams,
  ): Observable<PageResponse<DealResponse>> {
    let httpParams = new HttpParams({ encoder: new FullHttpParameterCodec() }).set(
      'businessId',
      params.businessId.toString(),
    );

    if (params.phonePrefix) {
      httpParams = httpParams.set('phonePrefix', params.phonePrefix);
    }
    if (params.idDealCycle !== undefined) {
      httpParams = httpParams.set('idDealCycle', params.idDealCycle.toString());
    }
    if (params.idCompany !== undefined) {
      httpParams = httpParams.set('idCompany', params.idCompany.toString());
    }
    if (params.idDealOrigin !== undefined) {
      httpParams = httpParams.set(
        'idDealOrigin',
        params.idDealOrigin.toString(),
      );
    }
    if (params.minAmount !== undefined) {
      httpParams = httpParams.set('minAmount', params.minAmount.toString());
    }
    if (params.maxAmount !== undefined) {
      httpParams = httpParams.set('maxAmount', params.maxAmount.toString());
    }
    if (params.closedOnly !== undefined) {
      httpParams = httpParams.set('closedOnly', params.closedOnly.toString());
    }
    if (params.activeOnly !== undefined) {
      httpParams = httpParams.set('activeOnly', params.activeOnly.toString());
    }
    if (params.searchName) {
      httpParams = httpParams.set('searchName', params.searchName);
    }
    if (params.contactSearchTerm) {
      httpParams = httpParams.set(
        'contactSearchTerm',
        params.contactSearchTerm,
      );
    }
    if (params.closedDateFrom) {
      httpParams = httpParams.set(
        'closedDateFrom',
        this.datetimeService.dateToUtcIso(params.closedDateFrom),
      );
    }
    if (params.closedDateTo) {
      httpParams = httpParams.set(
        'closedDateTo',
        this.datetimeService.dateToUtcIso(params.closedDateTo),
      );
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }
    if (params.userPropietyId !== undefined) {
      httpParams = httpParams.set(
        'userPropietyId',
        params.userPropietyId.toString(),
      );
    }
    if (params.idDealBusinessType !== undefined) {
      httpParams = httpParams.set(
        'idDealBusinessType',
        params.idDealBusinessType.toString(),
      );
    }
    if (params.idDealPriority !== undefined) {
      httpParams = httpParams.set(
        'idDealPriority',
        params.idDealPriority.toString(),
      );
    }
    if (params.idLead !== undefined) {
      httpParams = httpParams.set('idLead', params.idLead.toString());
    }
    if (params.estimatedCloseDateFrom) {
      httpParams = httpParams.set(
        'estimatedCloseDateFrom',
        params.estimatedCloseDateFrom,
      );
    }
    if (params.estimatedCloseDateTo) {
      httpParams = httpParams.set(
        'estimatedCloseDateTo',
        params.estimatedCloseDateTo,
      );
    }
    if (params.archived !== undefined) {
      httpParams = httpParams.set('archived', params.archived.toString());
    }

    return this.http.get<PageResponse<DealResponse>>(`${this.apiUrl}/deals`, {
      params: httpParams,
    });
  }

  // GET /api/deals/check-duplicate - Inc 11: avisa (no bloquea) si ya existe un negocio
  // con el mismo nombre (+ empresa/contacto/pipeline si se proveen).
  checkDuplicate(opts: {
    businessId: number;
    name: string;
    idCompany?: number;
    idContact?: number;
    idDealCycle?: number;
  }): Observable<DealDuplicateCheckResponse> {
    let params = new HttpParams()
      .set('businessId', opts.businessId.toString())
      .set('name', opts.name);
    if (opts.idCompany !== undefined) {
      params = params.set('idCompany', opts.idCompany.toString());
    }
    if (opts.idContact !== undefined) {
      params = params.set('idContact', opts.idContact.toString());
    }
    if (opts.idDealCycle !== undefined) {
      params = params.set('idDealCycle', opts.idDealCycle.toString());
    }
    return this.http.get<DealDuplicateCheckResponse>(
      `${this.apiUrl}/deals/check-duplicate`,
      { params },
    );
  }

  // PATCH /api/deals/{id}/archive - Inc 21: archiva el negocio
  archiveDeal(id: number, businessId: number): Observable<DealResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.patch<DealResponse>(
      `${this.apiUrl}/deals/${id}/archive`,
      null,
      { params },
    );
  }

  // PATCH /api/deals/{id}/unarchive - Inc 21: desarchiva el negocio
  unarchiveDeal(id: number, businessId: number): Observable<DealResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.patch<DealResponse>(
      `${this.apiUrl}/deals/${id}/unarchive`,
      null,
      { params },
    );
  }

  // GET /api/deals/{id} - Get deal by ID
  getDealById(id: number, businessId: number): Observable<DealResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<DealResponse>(`${this.apiUrl}/deals/${id}`, {
      params,
    });
  }

  // POST /api/deals - Create new deal (requires Sales Rep or higher)
  createDeal(deal: DealRequest): Observable<DealResponse> {
    return this.http.post<DealResponse>(`${this.apiUrl}/deals`, deal);
  }

  // PUT /api/deals/{id} - Update existing deal (requires Sales Rep or higher)
  updateDeal(id: number, deal: DealRequest): Observable<DealResponse> {
    return this.http.put<DealResponse>(`${this.apiUrl}/deals/${id}`, deal);
  }

  // DELETE /api/deals/{id} - Delete deal (requires Manager or higher)
  deleteDeal(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/deals/${id}`, { params });
  }

  // POST /api/deals/{id}/close - Close deal (won/lost) (requires Sales Rep or higher)
  closeDeal(
    id: number,
    businessId: number,
    idDealCycle: number,
    descriptionForClose?: string,
  ): Observable<DealResponse> {
    let params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('idDealCycle', idDealCycle.toString());

    if (descriptionForClose) {
      params = params.set('descriptionForClose', descriptionForClose);
    }

    return this.http.post<DealResponse>(
      `${this.apiUrl}/deals/${id}/close`,
      null,
      { params },
    );
  }

  // POST /api/deals/{id}/reopen - Reopen closed deal (requires Manager or higher)
  reopenDeal(
    id: number,
    businessId: number,
    idDealCycle: number,
  ): Observable<DealResponse> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('idDealCycle', idDealCycle.toString());

    return this.http.post<DealResponse>(
      `${this.apiUrl}/deals/${id}/reopen`,
      null,
      { params },
    );
  }

  // GET /api/deal-cycles - List all deal cycles
  getDealCycles(): Observable<DealCycle[]> {
    return this.http.get<DealCycle[]>(`${this.apiUrl}/deal-cycles`);
  }

  // GET /api/deal-origins - List all deal origins
  getDealOrigins(): Observable<DealOrigin[]> {
    return this.http.get<DealOrigin[]>(`${this.apiUrl}/deal-origins`);
  }

  // GET /api/deal-business-types?businessId= - List deal business types (v1.26) — paginated response
  getDealBusinessTypes(businessId: number): Observable<DealBusinessType[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('size', '200');
    return this.http
      .get<{
        content: DealBusinessType[];
      }>(`${this.apiUrl}/deal-business-types`, { params })
      .pipe(map((r) => r.content ?? []));
  }

  // GET /api/deal-priorities?businessId= - List deal priorities ordered by priorityOrder (v1.26) — paginated response
  getDealPriorities(businessId: number): Observable<DealPriority[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('size', '200');
    return this.http
      .get<{
        content: DealPriority[];
      }>(`${this.apiUrl}/deal-priorities`, { params })
      .pipe(map((r) => r.content ?? []));
  }

  // GET /api/auth/users/selectable?businessId - Lista de usuarios seleccionables para asignar
  // responsable. Endpoint sin permiso 'users:VIEW' (solo aislamiento por tenant), para que roles
  // como Ventas puedan poblar el selector; el listado completo /auth/users exige users:VIEW y da 403.
  getUsersByBusiness(business: number): Observable<CrmUserResponse[]> {
    const params = new HttpParams()
      .set('businessId', business.toString());
    const request = this.http
      .get<CrmUserResponse[]>(`${this.apiUrl}/auth/users/selectable`, { params })
      .pipe(
        map((r) => {
          return r ?? [];
        }),
      );
    return request;
  }

  // POST /api/deals/bulk-action - Acción masiva (CHANGE_OWNER | CHANGE_STAGE | DELETE) sobre deals.
  // businessId/userId salen del token (no enviar). Los fallos por deal vienen en 200 dentro de failures[].
  bulkActionDeals(req: DealBulkActionRequest): Observable<DealBulkActionResponse> {
    return this.http.post<DealBulkActionResponse>(
      `${this.apiUrl}/deals/bulk-action`,
      req,
    );
  }
}
